import { Button, Card, Col, Row, Spin, Typography, Space, Tooltip } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { eResultCode } from "../../../utils/enum";
import DateUtils from "../../../utils/date";
import { TCrateScanResult } from "../../../types";
import { TContext, TResponseModel } from "../../../types/config";
import {
  GetScannedCrateList,
  UpdateCrateMapping,
} from "../../../utils/api.constant";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  BarcodeOutlined,
  CalendarOutlined,
  BoxPlotOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClearOutlined,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import FormLayout, {
  FormContent,
  FormHeader,
} from "../../../components/form/form";
import { UserContext } from "../../../context";
import CustomModal from "../../../components/modal";

type TProps = {
  onCloseDrawer: () => void;
};

function CrateScannedHistory(props: TProps) {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [prevScannedResult, setPrevScannedResult] = useState<
    TCrateScanResult[]
  >([]);
  const [selectedCrate, setSelectedCrate] = useState<TCrateScanResult | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const context: TContext = useContext(UserContext);

  useEffect(() => {
    fetchPrevScanData();
  }, []);

  const fetchPrevScanData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedCrateList,
        requestPayload,
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setPrevScannedResult(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedCrate(null);
    setShowConfirmationModal(false);
  };

  // to clear all assigned blower from selected crate
  const handleClear = async () => {
    try {
      const payload = {
        data: {
          id: selectedCrate?.id, // value is 0 because no crate is required
          emptyCrate: true, // to unassigned the creates
        },
      };
      setIsLoading(true);

      const response = await post(UpdateCrateMapping, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchPrevScanData();
        setShowConfirmationModal(false);
        setSelectedCrate(null);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <FormHeader>
        <Row
          // gutter={16}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Col>
            <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
              Crate Scanned History
            </h3>
          </Col>
          <Col>
            <Button
              type="default"
              loading={isLoading}
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Close
            </Button>
          </Col>
        </Row>
      </FormHeader>
      <FormContent>
        <div
          style={{
            transition: "width 0.5s ease",
          }}
        >
          {isLoading ? (
            <Spin tip="Loading History..." />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                padding: "10px 0",
              }}
            >
              {prevScannedResult?.map((result, index) => (
                <Card
                  key={result.id}
                  size="small"
                  bodyStyle={{ padding: "1rem 1.25rem" }}
                  style={{
                    borderRadius: 6,
                    border: "1px solid #e5e5e5",
                    background: "#fff",
                    marginBottom: "1rem",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                  hoverable
                >
                  <Row gutter={[12, 6]}>
                    {/* Serial Number */}
                    <Col span={24}>
                      <Row align="middle" justify="space-between">
                        <Col>
                          <Space align="center">
                            <BarcodeOutlined
                              style={{ fontSize: 18, color: "#1677ff" }}
                            />
                            <Typography.Title
                              level={5}
                              style={{
                                margin: 0,
                                fontWeight: 500,
                                color: "#333",
                              }}
                            >
                              {result.serialNumber}
                            </Typography.Title>
                          </Space>
                        </Col>

                        <Col>
                          <Tooltip
                            title={
                              result.blowerCount === 0
                                ? "Empty crate"
                                : "Clear all"
                            }
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              disabled={result.blowerCount === 0 ? true : false}
                              icon={<ClearOutlined />}
                              onClick={() => {
                                setSelectedCrate(result);
                                setShowConfirmationModal(true);
                              }}
                            />
                          </Tooltip>
                        </Col>
                      </Row>
                    </Col>

                    {/* Crate Availability */}
                    <Col span={24}>
                      <Space size="small">
                        <BoxPlotOutlined style={{ color: "#595959" }} />
                        <Typography.Text style={{ fontSize: 14 }}>
                          {`Total ${result.blowerCount} out of ${result.crateCapacity} slots used`}
                        </Typography.Text>
                      </Space>
                    </Col>

                    {/* Optional: Progress Bar */}
                    <Col span={24}>
                      <div style={{ width: "100%", paddingRight: "1rem" }}>
                        <div
                          style={{
                            background: "#f0f0f0",
                            borderRadius: 4,
                            overflow: "hidden",
                            height: 8,
                          }}
                        >
                          <div
                            style={{
                              width: `${
                                (result.blowerCount / result.crateCapacity) *
                                100
                              }%`,
                              background: "#1677ff",
                              height: "100%",
                              transition: "width 0.3s ease",
                            }}
                          />
                        </div>
                      </div>
                    </Col>

                    {/* Scanned Date */}
                    <Col span={24}>
                      <Space size="small">
                        <CalendarOutlined style={{ color: "#8c8c8c" }} />
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 13 }}
                        >
                          Scanned on{" "}
                          {DateUtils.format(
                            result.scannedDate,
                            context?.user?.dateFormat,
                            true,
                          )}
                        </Typography.Text>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )}
        </div>
        <CustomModal
          title="Confirm Delete"
          isOpen={showConfirmationModal}
          onOk={handleClear}
          onCancel={() => handleCancel()}
          okText="Proceed"
          cancelText="Close"
          cusHeader={true}
        >
          <p>
            You are about to remove all the blowers from crate{" "}
            <b>{selectedCrate?.serialNumber}</b>?
          </p>
        </CustomModal>
      </FormContent>
    </FormLayout>
  );
}

export default CrateScannedHistory;
